import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Users } from "lucide-react";
import Button from "../common/Button";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { SplitText } from "gsap/all";

// gsap.registerPlugin(ScrollTrigger, SplitText);

const ValuePropositionSection = ({ onNavigate }) => {
  useGSAP(() => {
    /* -------------------------------------------
     TEXT SPLIT ANIMATION (ScrollTrigger)
    -------------------------------------------- */
    const headerSplit = new SplitText(".title", { type: "chars" });
    const paragraphSplit = new SplitText(".subtitle", { type: "lines" });

    gsap.from(headerSplit.chars, {
      yPercent: 100,
      opacity: 0,
      duration: 1.4,
      ease: "expo.out",
      stagger: 0.04,
      scrollTrigger: {
        trigger: "#employers",
        start: "top 80%",
        end: "top 20%",
        toggleActions: "play none none reverse",
      },
    });

    gsap.from(paragraphSplit.lines, {
      y: 40,
      opacity: 0,
      duration: 1.4,
      ease: "power3.out",
      stagger: 0.15,
      scrollTrigger: {
        trigger: "#employers",
        start: "top 70%",
        end: "top 10%",
        toggleActions: "play none none reverse",
      },
    });

    /* -------------------------------------------
     3D SCROLL TILT (GLOBAL)
    -------------------------------------------- */
    const cards = gsap.utils.toArray(".tilt-card");

    cards.forEach((card) => {
      gsap.to(card, {
        rotateX: 8,
        ease: "none",
        scrollTrigger: {
          trigger: card,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    /* -------------------------------------------------
      PARALLAX BACKGROUND FOR EACH CARD
    -------------------------------------------------- */
    const cardBGs = gsap.utils.toArray(".card-bg");

    cardBGs.forEach((bg) => {
      gsap.to(bg, {
        scale: 1.1,
        y: -40,
        ease: "none",
        scrollTrigger: {
          trigger: bg,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    /* -------------------------------------------------
      TITLE UNDERLINE CONTRAIL EFFECT
    -------------------------------------------------- */
    gsap.from("#contrail-path", {
      strokeDasharray: 200,
      strokeDashoffset: 200,
      duration: 2,
      ease: "expo.out",
      scrollTrigger: {
        trigger: "#employers",
        start: "top 85%",
        toggleActions: "play none none reverse",
      },
    });
  });

  /* -------------------------------------------
    CARD VARIANTS (Framer Motion)
  -------------------------------------------- */
  const containerVariant = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.3 },
    },
  };

  const cardVariant = {
    hidden: { opacity: 0, y: 90, scale: 0.9 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 120, damping: 18 },
    },
  };

  return (
    <section
      id="employers"
      className="py-20 bg-green-200 relative overflow-hidden"
    >
      {/* Animated smooth background gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50 opacity-70"
        initial={{ backgroundPosition: "0% 50%" }}
        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-3 title relative inline-block">
            Built for Aviation Excellence
            {/* CONTRAIL underline */}
            <svg
              width="180"
              height="20"
              className="mx-auto block mt-2"
              viewBox="0 0 160 20"
            >
              <path
                id="contrail-path"
                d="M5 10 C40 2, 120 18, 155 10"
                stroke="#3b82f6"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </h2>

          <p className="text-lg text-secondary-600 max-w-3xl mx-auto subtitle">
            Whether you're a professional seeking new heights or an employer
            building a world-class team, WinguPort provides the specialized
            tools you need to succeed.
          </p>
        </motion.div>

        {/* CARDS */}
        <motion.div
          className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
          variants={containerVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.3 }}
        >
          {/* PROFESSIONALS CARD */}
          <motion.div
            className="relative bg-secondary-800 text-white p-8 rounded-2xl overflow-hidden group shadow-lg tilt-card"
            variants={cardVariant}
            whileHover={{
              y: -12,
              scale: 1.02,
              boxShadow:
                "0px 20px 30px rgba(0,0,0,0.2), 0px 0px 25px rgba(59,130,246,0.5)",
            }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <motion.div
              className="absolute inset-0 bg-cover bg-center opacity-20 card-bg"
              style={{ backgroundImage: "url('/images/professionals-bg.jpg')" }}
            />

            <div className="relative z-10 flex flex-col h-full">
              <Users className="w-8 h-8 text-primary-400 mb-4" />

              <h3 className="text-2xl font-bold">For Aviation Professionals</h3>

              <p className="text-secondary-300 my-4">
                Access exclusive opportunities, build a standout profile, and
                connect with top aviation companies worldwide.
              </p>

              <Button
                variant="primary"
                onClick={() => onNavigate("/signup")}
                icon={<ArrowRight />}
                iconPosition="right"
              >
                Start Your Journey
              </Button>
            </div>
          </motion.div>

          {/* EMPLOYERS CARD */}
          <motion.div
            className="relative bg-primary-600 text-white p-8 rounded-2xl overflow-hidden group shadow-lg tilt-card"
            variants={cardVariant}
            whileHover={{
              y: -12,
              scale: 1.02,
              boxShadow:
                "0px 20px 30px rgba(0,0,0,0.2), 0px 0px 25px rgba(16,185,129,0.5)",
            }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <motion.div
              className="absolute inset-0 bg-cover bg-center opacity-20 card-bg"
              style={{ backgroundImage: "url('/images/employers-bg.jpg')" }}
            />

            <div className="relative z-10 flex flex-col h-full">
              <Briefcase className="w-8 h-8 text-white mb-4" />

              <h3 className="text-2xl font-bold">For Employers</h3>

              <p className="text-primary-100 my-4">
                Tap into a pool of verified aviation professionals and
                streamline your hiring process.
              </p>

              <Button
                variant="secondary"
                className="bg-white text-primary-600 hover:bg-primary-50"
                onClick={() => onNavigate("/pricing")}
                icon={<ArrowRight />}
                iconPosition="right"
              >
                Start Hiring
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValuePropositionSection;
